import { Attr, IndexedMesh } from "./indexed-mesh";
import { IndexedMeshWriter } from "./indexed-mesh-writer";


type FanAttrs = {
    uv: Attr,
    normal: Attr,
}

export class Fan extends IndexedMeshWriter<FanAttrs> {

    x: number;
    y: number;
    radius: number;
    start: number;
    end: number;

    n: number;

    /**
     * @param x - The X coordinate of the center of the disc
     * @param y - The Y coordinate of the center of the disc
     * @param radius
     * @param start - the angle at which to start the fan. Start should be less
     * than the end parameter if you want the normals to face +Z.
     * @param end - the angle at wihch to end the fan. End should be greater
     * than the start parameter if you want the normals to face +Z.
     * @param n - The count of vertices around the arc of the fan. The center
     * doesn't count, so the total count of vertices is n + 1.
     */
    constructor(x: number, y: number, radius: number, start: number, end: number, n: number) {
        super();
        this.x = x;
        this.y = y;
        this.start = start;
        this.end = end;
        this.radius = radius;
        this.n = n;
    }

    computeAttributeCounts(): IndexedMesh<FanAttrs> {
        const mesh = new IndexedMesh({
            uv: new Attr(2),
            normal: new Attr(3),
        });

        const
            n = this.n,
            triangles = n - 1,
            vertexes = n + 1;

        mesh.index.count = triangles * 3;
        mesh.position.count = vertexes;
        mesh.attrs.normal.count = vertexes;
        mesh.attrs.uv.count = vertexes;

        return mesh;
    }

    computeMeshData(mesh: IndexedMesh<FanAttrs>) {
        const
            x = this.x,
            y = this.y,
            n = this.n,
            r = this.radius,
            start = this.start,
            end = this.end,
            inc = (end - start) / (n - 1),
            pos = mesh.position.data,
            idx = mesh.index.data,
            uv = mesh.attrs.uv.data,
            norm = mesh.attrs.normal.data;

        // Manually fill in the center
        pos[0] = x;
        pos[1] = y;
        pos[2] = 0;
        pos[3] = x + r*Math.cos(start);
        pos[4] = y + r*Math.sin(start);
        pos[5] = 0;

        norm[0] = 0; norm[1] = 0; norm[2] = 1;
        norm[3] = 0; norm[4] = 0; norm[5] = 1;

        uv[0] = pos[0]; uv[1] = pos[1];
        uv[2] = pos[3]; uv[3] = pos[4];

        // j -> pos, norm; k -> idx; m -> uv
        let j = 6, k = 0, m = 4;
        for(let i = 1; i < n; i++) {
            const
                t = start + i*inc,
                vx = x + r*Math.cos(t),
                vy = y + r*Math.sin(t);

            idx[k]  = 0;  idx[k+1]  = i;  idx[k+2]  = i+1;
            pos[j]  = vx; pos[j+1]  = vy; pos[j+2]  = 0;
            norm[j] = 0;  norm[j+1] = 0;  norm[j+2] = 1;
            uv[m]   = vx; uv[m+1]   = vy;

            j += 3;
            k += 3;
            m += 2;
        }

        //idx[k] = 0; idx[k+1] = n; idx[k+2] = 1;
    }
}
